import dayjs from "dayjs";
import { Temporal } from "temporal-polyfill";
import { t } from "@lingui/core/macro";

import { i18n } from "_/i18n";

import {
  NON_BREAKING_DASH,
  NON_BREAKING_SPACE,
  UNKNOWN_VALUE_STRING,
} from "./consts";

/** Time zone to show times in: the browser's local time zone, or UTC. */
export type DisplayTimeZone = "local" | "utc";

/** A date/time value. If a number, must be msecs since epoch. */
export type DateTimeValue = Date | string | number;

export interface DateTimeFormatOptions {
  /** Value to format. If a number, must be msecs since epoch. */
  value: DateTimeValue | undefined | null;
  /** What to show if the value is undefined, null, or invalid */
  defaultValue?: string;
  /**
   * Whether to use regular or non-breaking delimiters in the following places:
   * * spaces between date and time
   * * dashes between YYYY-MM-DD
   * * spaces between beginning and ending of a range.
   *
   * Values supported are:
   * * `"none"` - line breaks are allowed everywhere.
   * * `"all"` - the entire output is non-breaking.
   * * `"date-only"` - output will not break dates but will break between date
   *   and time, and between start/end of ranges
   * * `"break-range"` - each date/time in a range will be non-breaking, but the
   *   output may break at the " - " that delimits start and end of the range.
   *
   * The default is:
   * * `"none"` for string-returning functions
   * * `"date-only"` for the `<DateOnly>` and `<DateTime>` components
   * * `"break-range"` for the `<DateTimeRange>` component.
   *
   * Be careful when using non-`"none"` values for the `dateOnlyString`,
   * `dateTimeString`, or `dateTimeRangeString` functions to produce output that
   * end-users can copy to the clipboard, because strings with non-breaking
   * hyphens or spaces won't be parseable by other systems that expect normal
   * delimiters.
   *
   * This is not a concern when using the <DateOnly>, <DateTime>, or
   * <DateTimeRange> components, because they render the output with
   * non-breaking-ness enforced by CSS, not by changing characters in the
   * string. When doing your own rendering of user-copyable date/time strings,
   * you should also use CSS not delimiter characters to prevent line breaks.
   * */
  nowrap?: "none" | "date-only" | "break-range" | "all";
  /** How much time to show */
  precision?: "minute" | "second" | "millisecond";
  /** Time zone to show the value in. Defaults to the browser's time zone. */
  timeZone?: DisplayTimeZone;
}

export function timeZoneId(timeZone: DisplayTimeZone = "local"): string {
  return timeZone === "utc" ? "UTC" : Temporal.Now.timeZoneId();
}

/**
 * Convert a date/time value to wall-clock date and time in the given time
 * zone, or `null` if the value isn't a valid date/time.
 */
export function toPlainDateTime(
  value: DateTimeValue,
  timeZone: DisplayTimeZone = "local"
): Temporal.PlainDateTime | null {
  const epochMs = new Date(value).getTime();
  if (Number.isNaN(epochMs)) {
    return null;
  }
  // `fromEpochMilliseconds` rejects fractional values, which come up when a
  // time range is split into equal parts (e.g. chart buckets).
  return Temporal.Instant.fromEpochMilliseconds(Math.floor(epochMs))
    .toZonedDateTimeISO(timeZoneId(timeZone))
    .toPlainDateTime();
}

type DateOnlyOptions = Omit<DateTimeFormatOptions, "precision">;

/**
 * Display a date (only) in ISO 8601 format (YYYY-MM-DD, e.g. 2024-01-24)
 * that's the standard format in Basis, in the browser's time zone unless
 * `timeZone` says otherwise. If rendering a date, use the <DateOnly>
 * component.
 */
export const dateOnlyString = ({
  value,
  defaultValue,
  nowrap = "none",
  timeZone,
}: DateOnlyOptions) => {
  const plainDateTime = value == null ? null : toPlainDateTime(value, timeZone);
  if (!plainDateTime) {
    return defaultValue ?? UNKNOWN_VALUE_STRING;
  }
  const dateDelimiter = ["all", "break-range", "date-only"].includes(nowrap)
    ? NON_BREAKING_DASH
    : "-";
  return plainDateTime.toPlainDate().toString().replaceAll("-", dateDelimiter);
};

type TimeOnlyOptions = Omit<DateTimeFormatOptions, "nowrap">;

/**
 * Display a time (only) in 24-hour time format (HH:mm, e.g. 14:52) which is the
 * standard format in Basis, in the browser's time zone unless `timeZone` says
 * otherwise. If rendering a time, use the <TimeOnly> component.
 */
export const timeOnlyString = ({
  value,
  defaultValue,
  precision = "minute",
  timeZone,
}: TimeOnlyOptions) => {
  const plainDateTime = value == null ? null : toPlainDateTime(value, timeZone);
  if (!plainDateTime) {
    return defaultValue ?? UNKNOWN_VALUE_STRING;
  }
  // e.g. "14:52", "14:52:07", or "14:52:07.123"
  return plainDateTime.toPlainTime().toString({ smallestUnit: precision });
};

/**
 * Display a date and time in ISO 8601 format (YYYY-MM-DD, e.g. 2024-01-24
 * 14:52) that's the standard format in Basis, in the browser's time zone
 * unless `timeZone` says otherwise.
 *
 * If rendering a date and time as HTML, use the <DateTime> component which will
 * handle line-breaking with CSS which is better than changing the characters in
 * the string which may cause problems if the string is copied to the clipboard
 * and pasted into some other system that tries to parse it as an ISO 8601
 * date/time.
 *
 * If you do want to use the output of this function for content rendered to
 * HTML, you'll generally want to use `nowrap=false` unless the string is
 * rendered in a context that cannot be copied to the clipboard, like a `title`
 * attribute or a <select> option.
 */
export const dateTimeString = (options: DateTimeFormatOptions) => {
  const { value, defaultValue, nowrap } = options;
  if (value == null) {
    return defaultValue ?? UNKNOWN_VALUE_STRING;
  }
  const space = nowrap ? NON_BREAKING_SPACE : " ";
  return `${dateOnlyString(options)}${space}${timeOnlyString(options)}`;
};

/** Whether two date/times fall on the same calendar day in a time zone. */
export const isSameDay = (
  d1: DateTimeValue,
  d2: DateTimeValue,
  timeZone: DisplayTimeZone = "local"
) => {
  const date1 = toPlainDateTime(d1, timeZone)?.toPlainDate();
  const date2 = toPlainDateTime(d2, timeZone)?.toPlainDate();
  return date1 != null && date2 != null && date1.equals(date2);
};

export interface DateTimeRangeFormatOptions extends Omit<
  DateTimeFormatOptions,
  "value"
> {
  /** Value to format. If a number, must be msecs since epoch. */
  start: DateTimeValue | undefined | null;
  /** Value to format. If a number, must be msecs since epoch. */
  end: DateTimeValue | undefined | null;
}

/**
 * Display a date/time range in ISO 8601 format. If start and end dates are the
 * same, then omit the second date. Otherwise show both dates. It's the caller's
 * responsibility to ensure that `start` is before `end`.
 *
 * Examples: "2024-01-24 14:52 - 15:52", "2024-01-24 14:52 - 2024-01-24 15:23"
 */
export const dateTimeRangeString = ({
  start,
  end,
  defaultValue,
  nowrap = "none",
  timeZone,
}: DateTimeRangeFormatOptions) => {
  if (start == null) {
    return defaultValue ?? UNKNOWN_VALUE_STRING;
  }
  const space = ["all", "break-range"].includes(nowrap)
    ? NON_BREAKING_SPACE
    : " ";
  const startString = dateTimeString({
    value: start,
    defaultValue,
    nowrap,
    timeZone,
  });
  if (end == null) return `${startString}${space}${NON_BREAKING_DASH}`;
  const endString = isSameDay(start, end, timeZone)
    ? timeOnlyString({ value: end, timeZone })
    : dateTimeString({ value: end, nowrap, timeZone });
  return `${startString}${space}${NON_BREAKING_DASH}${space}${endString}`;
};

export interface RelativeDateTimeOptions extends Pick<
  DateTimeFormatOptions,
  "value" | "defaultValue"
> {
  /** Whether to show "Now" instead of relative time */
  isNow?: boolean;
}

/**
 * Get a relative time string (e.g. "2 hours ago") from the current time.
 *
 * To show "Now" instead of of a relative time for recent events, use the
 * `isNow` prop.
 *
 * If rendering this string, use the `<RelativeDateTime>` component which adds a
 * tooltip listing the actual time.
 */
export const relativeDateTimeString = ({
  value,
  defaultValue,
  isNow,
}: RelativeDateTimeOptions) => {
  if (isNow) {
    return t`common.now`;
  }
  if (value == null) {
    return defaultValue ?? UNKNOWN_VALUE_STRING;
  }
  return dayjs(new Date(value)).fromNow();
};

/**
 * Format a duration of a print, using a human-readable string like "2h 5m".
 */
export const formatHourMinuteDuration = (
  duration: Temporal.Duration | undefined | null
): string => {
  if (!duration) {
    return UNKNOWN_VALUE_STRING;
  }
  // Normally show hours/minutes for print durations, but if it
  // just started show seconds only.
  const underOneMinute = duration.total({ unit: "second" }) < 60;
  const truncated = duration.round({
    largestUnit: underOneMinute ? "seconds" : "hours",
    smallestUnit: underOneMinute ? "seconds" : "minutes",
    roundingMode: "trunc",
  });
  return truncated.toLocaleString(i18n.locale, {
    style: "narrow",
  });
};
